interface GeocodeResult {
  lat: number;
  lng: number;
  address: string;
  formatted_address: string;
  place_id?: string;
  types?: string[];
}

interface GeocodeResponse {
  success: boolean;
  data?: GeocodeResult;
  error?: string;
}

class GeocodingService {
  private baseUrl = '/api/v1/geocoding';

  // Forward geocoding: address -> coordinates
  async geocodeAddress(address: string): Promise<GeocodeResult | null> {
    try {
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ address }),
      });

      const result: GeocodeResponse = await response.json();

      if (result.success && result.data) {
        return result.data;
      } else {
        console.error('Geocoding error:', result.error);
        return null;
      }
    } catch (error) {
      console.error('Geocoding service error:', error);
      return null;
    }
  }

  // Reverse geocoding: coordinates -> address
  async reverseGeocode(lat: number, lng: number): Promise<GeocodeResult | null> {
    try {
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ lat, lng, language: 'vi' }),
      });

      const result: GeocodeResponse = await response.json();

      if (result.success && result.data) {
        return result.data;
      } else {
        console.error('Reverse geocoding error:', result.error);
        return null;
      }
    } catch (error) {
      console.error('Reverse geocoding service error:', error);
      return null;
    }
  }

  // Batch geocoding for multiple addresses
  async batchGeocodeAddresses(addresses: string[]): Promise<(GeocodeResult | null)[]> {
    const promises = addresses.map(address => this.geocodeAddress(address));
    return Promise.all(promises);
  }

  // Batch reverse geocoding for multiple coordinates
  async batchReverseGeocode(
    coordinates: { lat: number; lng: number }[]
  ): Promise<(GeocodeResult | null)[]> {
    const promises = coordinates.map(coord => this.reverseGeocode(coord.lat, coord.lng));
    return Promise.all(promises);
  }

  // Get coordinates from address with fallback
  async getCoordinates(address: string): Promise<{ lat: number; lng: number } | null> {
    const result = await this.geocodeAddress(address);
    if (result) {
      return { lat: result.lat, lng: result.lng };
    }
    return null;
  }

  // Get address from coordinates with fallback
  async getAddress(lat: number, lng: number): Promise<string | null> {
    const result = await this.reverseGeocode(lat, lng);
    if (result) {
      return result.formatted_address;
    }
    return null;
  }

  // Validate coordinates
  isValidCoordinates(lat: number, lng: number): boolean {
    return (
      !isNaN(lat) &&
      !isNaN(lng) &&
      isFinite(lat) &&
      isFinite(lng) &&
      lat >= -90 &&
      lat <= 90 &&
      lng >= -180 &&
      lng <= 180
    );
  }

  // Validate address
  isValidAddress(address: string): boolean {
    return typeof address === 'string' && address.trim().length > 0;
  }
}

export const geocodingService = new GeocodingService();
export default geocodingService;
